import extractLastOptions from "../../../utils/extract-last-options";
import getFunctionData from "../../../utils/get-function-data";
import { G_namedProperties } from "../../../utils/globals/named-properties";
import getProgressCoordinates from "../../animate/utils/get-progress-coordinates";
import getProgressTime from "../../animate/utils/get-progress-time";
import checkPauseAndIterations from "./check-pause-and-iterations";
import getDefaultOptions from "./get-default-options";
import getLayers from "./get-layers";
import getMergeable from "./get-mergeable";
import getOptions from "./get-options";
import mergeOptions from "./merge-options";
import setAction from "./set-action";
import setReference from "./set-reference";
import setTransformOrder from "./set-transform-order";

/**
 * Get parent property data
 * @param {PropertyWrapperProps} parent
 * @param {MainClassProps | null} main
 * @param {StepProps} step
 * @param {TriggerProps | null} [trigger]
 */
const getParentMap = (parent, main, step, trigger) => {
  const map = new Map();

  /** Get the parent name. (transform, opacity, ...) */
  const name = parent.get("name") || "";

  /** Get parameters. Another inner function (`translateX`) or values */
  const parameters = parent.get("parameters") || "";

  /** Parent timeline type ("timeline" or "cluster") */
  const type = /** @type {String} */ (step.get("timeline-type"));

  /** Extract the available options from the string. (`{'options' => '<1s>', 'rest' => 'translateX(2rem)'}`) */
  const extracted = extractLastOptions(parameters);

  const defaults = /** @type {OptionsListMapProps} */ (getDefaultOptions());

  /** Global (timeline) options */
  const global = /** @type {OptionsListMapProps} */ (
    step.get("common-options") || null
  );

  /** Common step or timeline options */
  const common = step.get("options");

  /** Get the parent options */
  const local = /** @type {OptionsListMapProps} */ (
    getOptions(extracted.get("options"), main, 0)
  );

  const mergeable = getMergeable(global, common, defaults, type);

  const options = mergeOptions(local, mergeable);

  /** Get the duration for duration divide */
  // const duration = /** @type {DefaultOptionMapProps} */ (
  //   options?.get("duration")
  // );

  /** Duration divide */
  // const divide = duration?.get("duration-divide");

  /**
   * The rest of the extracted string.

   */
  const rest = extracted.get("rest");

  /**
   * Get the potential inner function data.
   * @returns `{'name' => 'blur', 'parameters' => '3px', 'isFn' => true, 'isComposition' => false}`
   */
  const restData = getFunctionData(rest);

  /** Check if the inner function name is in "named properties" (transform, filter,..) */
  const isNamed = G_namedProperties.has(name);

  /** Set function name or `0` */
  const fnName = isNamed ? restData.get("name") : "0";

  /**
   * Whole path to the last-level pure value
   * @example `transform>N:translateX>`
   */
  const path = `${name}>N:${fnName}>:`;

  /**
   * Priority (!important)
   * @example `!translateX(2px)`
   */
  const priority = parent.get("priority") || false;

  map.set("original", rest);
  map.set("options", options);
  map.set("priority", priority);

  /** Get the layers separated by the `╳` */
  const layers = getLayers(map, main, path, step, name, fnName, trigger);

  const stepParents =
    /** @type {Map<String, any>} */ (step.get("properties")) || new Map();

  /** The parent property name does not exists in the steps */
  if (!stepParents.has(name)) {
    stepParents.set(name, new Map());
  }

  /** Parent name ("transform") */
  const stepParentsName = stepParents.get(name);

  /** The inner property name does not exists in parent property */
  if (!stepParentsName.get(fnName)) {
    stepParentsName.set(fnName, new Map());
  }

  /** Parent inner function name ("translateY", "0") */
  const stepFnName = stepParents.get(name).get(fnName);

  /** Default property step data */
  stepFnName.set("original", rest || null);
  stepFnName.set("options", options);
  stepFnName.set("layers", layers);
  stepFnName.set("priority", priority);
  // stepFnName.set("duration-divide", divide);
  stepFnName.set("offset", step.get("offsets") || null);
  stepFnName.set("divider", isNamed ? " " : ", ");
  stepFnName.set("pure", []);
  stepFnName.set("should-process", true);
  stepFnName.set("is-property", true);
  stepFnName.set("is-coordinates-based", false);
  stepFnName.set("is-active", true);
  stepFnName.set("status-enter-started", false);
  stepFnName.set("status-enter-finished", false);
  stepFnName.set("status-leave-started", false);
  stepFnName.set("status-leave-finished", false);
  stepFnName.set("last-overall", 0);
  stepFnName.set("done", false);

  setAction(stepFnName, name, rest, parameters, global, local);
  setReference(stepFnName, parent, trigger, options);

  if (main) {
    const parentRefs = /** @type {StorageParentRefsProps} */ (
      main.storage.get("parent-refs")
    );

    /** Set this inner function to the storage to add a "pure" map and options refs */
    parentRefs.push(stepFnName);
  }

  if (trigger) {
    const isOffsetPath =
      local?.has("is-offset-path") || global?.has("is-offset-path");
    const coordinates = trigger.get("is-coordinates-based");
    const fn = coordinates ? getProgressCoordinates : getProgressTime;

    stepFnName.set("get-progress", fn);
    stepFnName.set("is-coordinates-based", true);

    trigger.set("has-offset-path", isOffsetPath || false);

    checkPauseAndIterations(options);
  } else {
    stepFnName.set("last-value", "");
  }

  return step.get("properties");
};

export default getParentMap;
